import WRAP from "WRAP-JS";
import { _orderBy, formatUTCDate, isEmpty, parseUTCDate } from "../../../utils/FPEUtils";
import LegendSigmet from "../legend/LegendSigmet";
import { configSigmetAddingLayer, configSigmetDh, configSigmetLayer } from "./config/ConfigSigmet";
import { WX_ID_SIGMET } from "../../../store/WeatherContentsState";

class Sigmet {
  static NAME = WX_ID_SIGMET;
  dh;
  layer;
  ready = false;
  altitude;
  baseTime = '---';
  dispatcher;
  unreadDispatcher;
  labelPattern = 'SIGMET_TYPE';
  visible;

  constructor(altitude) {
    this.altitude = altitude;
    this.layer = new WRAP.Geo.Layer(Sigmet.NAME);
    this.layer.setVisible(false);
  }

  setDispatcher(dispatcher) {
    this.dispatcher = dispatcher;
  }

  setUnreadDispatcher(unreadDispatcher) {
    this.unreadDispatcher = unreadDispatcher;
  }

  async initialize() {
    await this.initData();
    this.initLayer();
    this.ready = true;
  }

  initData() {
    return new Promise(resolve => {
      this.dh = new WRAP.Data(configSigmetDh);

      this.dh.inspect((ref) => {
        this._filterWRAPData(ref);
        this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
        resolve();
      })
    });
  }

  initLayer() {
    this.layer.configure(this.dh, configSigmetLayer);
    this.layer.updated(this.addingDrawLayer.bind(this));
    this.layer.setTooltip((geo, data) => {
      if (data) {
        let type = '';
        let hr = '';
        if ( this.labelPattern === 'SIGMET_TYPE') {
          type = getSigmetText(data);
          hr = '<hr>';
        } else {
          const sigmetType = getSigmetTypeByBltin(data.bltin);
          if (!isEmpty(sigmetType)){
            if (!isEmpty(sigmetType.label)) {
              type = sigmetType.label;
              hr = '<hr>';
            }
          }
        }

        const bltn = data.bltin.replace(/\n{1,}/g, '<br>')
        return `<div style="text-align: left; max-width: 250px;">${type}${hr}${bltn}</div>`;
      }
    });
    this.layer.setAttributes({
      filter: this._visibleFilter.bind(this)
    });
  }

  async drawLayer(visible) {
    this.visible = visible;
    if (!this.ready) {
      await this.initialize();
    }
    this.setVisible(this.visible);
  }

  addingDrawLayer() {
    /*
     * Line,Area,PointにSIGMET分類を描画する(SIGMET分類はbltinに設定されているキーワードから判定する)
     * SIGMETの高度を描画する。
     * 解読不可SIGMET(Unanalysable)を通知する
     */
    const data = this.dh.retained.data.features;
    const layer = this.layer;
    const config = configSigmetAddingLayer['default'];
    const altitudes = [];
    const unAnalyzable = [];

    for (const feature of data) {
      if (!this._visibleFilter(feature)) {
        continue;
      }
      const elemType = feature.properties.elm_type;
      let offsetY = 0;
      if (feature.geometry) {
        if (feature.geometry.type === 'Point') {

          const point = feature.geometry.coordinates;

          if (['Line', 'Area', 'Point'].includes( elemType)) {
            //draw sigmet type
            const sigmetType = getSigmetTypeByBltin(feature.properties.bltin);

            if (!isEmpty(sigmetType)) {

              if (sigmetType.type === 'skip') {
                continue;
              } else {
                //SIGMET分類をテキストで表示するパターン

                const labelConf = elemType === 'Point' ? config.sigmetTypePoint :  config.sigmetTypeText;
                offsetY = labelConf.offsetY + (labelConf.adjustOffsetAltitude || 0);

                const text = this.labelPattern === 'SIGMET_TYPE' ? sigmetType.label : getSigmetText(feature.properties);
                const sigmetTypeLabel = new WRAP.Geo.Feature.Text(
                  Object.assign({
                    fontSize: 13
                  }, labelConf, {
                    point: point,
                    text: text
                  }));
                sigmetTypeLabel.data = feature.properties;

                layer.addFeature(sigmetTypeLabel);

                offsetY += labelConf.fontSize || 13;
              }

            } else if (elemType === 'Point') {
              offsetY = 19;
            }

          } else {
            //アイコンを表示するデータの高度ラベルオフセットを設定から取得
            let conf;
            switch (elemType) {
              case "MountainWave":
                conf = config.sigmetTypeMTW_OBS;
                break;
              case "Typhoon":
                conf = config.sigmetTypeTC_OBS;
                break;
              case "RadioAct":
                conf = config.sigmetTypeRDOACT;
                break;
              default:
                conf = {}
            }
            const sigmetTypeLabel = new WRAP.Geo.Feature.Text(
              Object.assign({
                fontSize: 13,
              }, config.sigmetTypeText, {
                point: point,
                text: getSigmetText(feature.properties),
                offsetY: conf.height
              }));
            sigmetTypeLabel.data = feature.properties;
            layer.addFeature(sigmetTypeLabel);
            offsetY = config.sigmetTypeText.fontSize + conf.height + (conf.adjustOffsetAltitude || 0);
          }

          //高度オブジェクト作成(最後に描画する)
          const altitude = getAltitude(feature.properties);
          if (!isEmpty(altitude)) {
            const altitudeConf = config.altitude;
            const altitudeLabel = new WRAP.Geo.Feature.Text(Object.assign({}, altitudeConf, {
              point: point,
              text: altitude,
              offsetY: offsetY
            }));
            altitudeLabel.data = feature.properties;
            altitudes.push(altitudeLabel)
          }
        }
      } else {
        if (elemType === 'Unanalysable') {
          unAnalyzable.push(feature.properties);
        }
      }
    }

    //draw altitude
    altitudes.forEach(altitude => {
      layer.addFeature(altitude);
    });

    this.unreadDispatcher(unAnalyzable);
  }

  _filterWRAPData(ref) {
    let data = ref.data.retained.data;
    //TODO データ取得エラー時の挙動
    if (!data) return;

    const now = Date.now();
    //重複データ除外用変数
    let duplicateCoordinate = {};

    //Polygon重複データ除外用変数
    let duplicatePolygonCoordinate = new Set();

    const _getDispKey = function (prop) {
      const elm = prop.elm_type;
      const sigType = (getSigmetTypeByBltin(prop.bltin) || {}).keyword;
      const fl = getAltitude(prop);
      return [elm, sigType, fl].join(":");
    };

    const f1 = (o) => {
      //valid range filtering
      let validBeg = o.properties.valid_beg;
      let validEnd = o.properties.valid_end;

      if (validBeg && validEnd) {
        validBeg = parseUTCDate(validBeg).getTime();
        validEnd = parseUTCDate(validEnd).getTime();
        return (validBeg <= now && now <= validEnd);
      } else {
        return false;
      }
    }
    const f2 = (o) => {
      return !isIgnoreSigmet(o.properties.bltin);
    }
    const f3 = (o) => {
      //filter duplicate point
      if (o.geometry && o.geometry.type === 'Point') {
        const dupKey = o.geometry.coordinates.join(':');
        if (duplicateCoordinate.hasOwnProperty(dupKey)) {
          const key = _getDispKey(o.properties);
          if (duplicateCoordinate[dupKey].includes(key)) {
            return false;
          } else {
            duplicateCoordinate[dupKey].push(key);
          }
        } else {
          duplicateCoordinate[dupKey] = [_getDispKey(o.properties)];
        }
      }
      return true;
    }
    const f4 = (o) => {
      if (o.properties && o.properties.elm_type === 'Area') {
        const dupKey = o.geometry.coordinates.join(':');
        if (duplicatePolygonCoordinate.has(dupKey)) {
          return false;
        } else {
          duplicatePolygonCoordinate.add(dupKey);
        }
      }
      return true;
    }
    const filters = [f1, f2, f3, f4];
    //coordinatesによる場所取りのためannounced_dateの降順でソートしておく
    const tmpFeatures = _orderBy(data.features, ["properties.announced_date"], ['desc']);
    const filteredFetures = tmpFeatures.filter(v => filters.every(f => f(v)));
    //古いものから描画するためannounced_dateの昇順でソートする
    data.features = _orderBy(filteredFetures, ["properties.announced_date"], ['asc']);

    //Areaにはsigmet種別による塗り分けをするのでsigmet種別を付与
    data.features.forEach(o => {
      if (o.properties && o.properties.elm_type === 'Area') {
        const sigmetType = getSigmetTypeByBltin(o.properties.bltin);
        const dataType = o.properties.data_type || 'undefined';
        if (sigmetType) {
          o.properties.sigmet_kind = `${sigmetType.keyword}_${dataType}`;
        }
      }
    });

    const maxTime = data.features.reduce((a, b) => {
      const t = b.properties.announced_date;
      return a > t ? a : t;
    }, 0);

    this.baseTime = formatUTCDate(parseUTCDate(maxTime), '%Y/%m/%d %H:%MZ');
  }

  _visibleFilter(feature) {
    const fl1 = feature.properties.flight_level_feet_1st;
    const fl2 = feature.properties.flight_level_feet_2nd;

    if (!fl1 && !fl2) {
      return true;
    }
    const low = fl2 ? fl2 / 100 : -1;
    const high = fl1 ? fl1 / 100 : -1;
    if (high !== -1 && low === -1 && this.altitude[0] > high) {
      return false;
    } else if (high === -1 && low !== -1 && this.altitude[1] < low) {
      return false;
    } else if (high !== -1 && low !== -1) {
      if (this.altitude[0] > high || this.altitude[1] < low) {
        return false;
      }
    }
    return true;
    // return (this.altitude[0] <= low && low <= this.altitude[1]) || (this.altitude[0] <= high && high <= this.altitude[1]);
  }

  getLayer() {
    return this.layer;
  }

  setVisible(visible) {
    if (!this.ready) return;

    if (this.layer.visible() === visible) {
      return;
    }
    if (visible) {
      this.layer.show();
    } else {
      this.layer.hide();
    }
    this.dh.setAutoUpdate('data', visible);
    this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
  }

  getLegend() {
    return {
      id: Sigmet.NAME,
      label: 'SIGMET',
      component: LegendSigmet,
      contents: this
    };
  }

  getTimeInformation() {
    return {
      id: Sigmet.NAME,
      label: 'SIGMET',
      baseTime: this.baseTime
    }
  }

  applyAltitude(altitude) {
    this.altitude = altitude;
    this.layer.invalidate();
    this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
  }

  changeRoutePointEditing(routePointEditing) {
    this.layer.disableInteraction(routePointEditing);
  }

  _getSelectedFlightLevel() {
    const low = Math.min(...this.altitude);
    const high = Math.max(...this.altitude);
    let fl;
    if(low === 0) {
      fl = 'SFC';
    } else {
      fl = `FL${('000' + low).slice(-3)}`;
    }
    if(low !== high) {
      fl += ` - FL${('000' + high).slice(-3)}`;
    }
    return fl;
  }
}


export default Sigmet;

//SIGMET分類定義
const SIGMET_TYPE = [
  {keyword: "TSGR", label: "TSGR", type: "text"},
  {keyword: "TS", label: "TS", type: "text"},
  {keyword: "TURB", label: "TURB"},
  {keyword: "ICE", label: "ICE", type: "text"},
  {keyword: "DS", label: "DS", type: "text"},
  {keyword: "SS", label: "SS", type: "text"},
  {keyword: "TC", type: "skip"},
  {keyword: "MTW", type: "skip"},
  {keyword: "RDOACT", type: 'skip'},
  {keyword: "CB", label: "CB", type: "text"}
];

const getSigmetTypeByBltin = (bltin) => {
  const str = bltin.replace(/\n/g, ' ');
  const arr = str.split(' ');
  let sigmetType = undefined;
  for (let i = 0; i < SIGMET_TYPE.length; i++) {
    const type = SIGMET_TYPE[i];
    if (arr.includes(type.keyword)) {
      sigmetType = type;
      break;
    }
  }
  return sigmetType;
};

//非表示にするSIGMET
const IGNORE_SIGMET = ["VA"];

const isIgnoreSigmet = (bltin) => {
  const str = bltin.replace(/\n/g, ' ');
  const arr = str.split(' ');
  let ignore = false;

  for (let i = 0; i < IGNORE_SIGMET.length; i++) {
    const type = IGNORE_SIGMET[i];
    if (arr.includes(type)) {
      ignore = true;
      break;
    }
  }
  return ignore;
}

const getSigmetText = (prop) => {
  let f = '', e = '';
  const sigmetId = prop.sigmet_id;
  const areaIndicator = prop.area_indicator;

  if (!isEmpty(sigmetId)) {
    f = sigmetId.replace(/[0-9]/g, "").trim();
    e = sigmetId.replace(/[^0-9]/g, "").trim();
  }
  return f + '/' + areaIndicator + ' ' + e;
}

const getAltitude = (prop) => {
  const fl1 = prop.flight_level_feet_1st;
  const fl2 = prop.flight_level_feet_2nd;

  if (!fl1 && !fl2) {
    return '';
  }

  const low = fl2 ? fl2 / 100 : ' - ';
  const high = fl1 ? fl1 / 100 : ' - ';

  if (low === high) {
    return 'FL' + low;
  } else {
    if (low === 0) {
      return 'SFC/FL' + high;
    }
    return 'FL' + low + '/' + high;
  }
}